// Speech for cards. Recorded clips first (S4: the language kit's audio, VOICEVOX, keyed by the
// text spoken; scripts/kit-audio.mjs, copied next to the pack and precached), so cards are read
// aloud offline even on a phone with no voice for the language. Otherwise the browser's
// speechSynthesis in the trip's language: the language kit's locale (D3), set by the app when the
// trip loads; on Android that works offline only if the language's voice data is installed.
import { playFile, type Playing } from './synth'

/** Make-up gain for recorded card clips (npm run audio:report: ~10 dB under the place voices). */
export const CARD_GAIN_DB = 9

let voice: SpeechSynthesisVoice | null | undefined
// Until a trip loads: the phone's own language (never assume Japanese).
let locale = typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US'

function pickVoice(): SpeechSynthesisVoice | null {
  if (typeof speechSynthesis === 'undefined') return null
  const voices = speechSynthesis.getVoices()
  const lang = locale.split('-')[0]!
  return voices.find((v) => v.lang === locale && v.localService) ?? voices.find((v) => v.lang.startsWith(lang)) ?? null
}

// The voice list arrives late on Chrome (voiceschanged), and never on a phone with none: until it
// has come or a moment has passed, "no voice" isn't known yet (WI-26).
const VOICES_SETTLE_MS = 2000
let voicesSettled = false
const listeners = new Set<() => void>()
const settle = () => {
  voicesSettled = true
  voice = pickVoice()
  for (const l of listeners) l()
}
if (typeof speechSynthesis !== 'undefined') {
  speechSynthesis.addEventListener?.('voiceschanged', settle)
  setTimeout(settle, VOICES_SETTLE_MS)
} else voicesSettled = true

/** Calls `cb` when what voices the phone has may have changed. Returns the unsubscribe. */
export function subscribeVoices(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

let clips: Record<string, string> = {}
let clipBase = ''
let playing: HTMLAudioElement | null = null
/** The clip playing through the audio bus, and a counter so a late-loading clip doesn't talk over the next. */
let current: Playing | null = null
let sayN = 0

/** The trip's recorded card audio: `base` + a manifest's clips (text → file). Empty: TTS only. */
export function setClips(base: string, next: Record<string, string>) {
  clipBase = base
  clips = next
  for (const l of listeners) l()
}

/** The pack's audio manifest, if it has one (in-app trips don't yet). Never throws. */
export async function loadClips(packId: string): Promise<void> {
  try {
    const res = await fetch(`/packs/${packId}/audio/manifest.json`)
    if (!res.ok) return setClips('', {})
    const m = (await res.json()) as { clips?: Record<string, string> }
    setClips(`/packs/${packId}/audio/`, m.clips ?? {})
  } catch {
    setClips('', {})
  }
}

/** A recorded clip exists for this text. */
export const hasClip = (text: string) => !!clips[text]

/** The trip's language for speech (BCP 47, e.g. ja-JP, ko-KR). */
export function setSpeechLocale(next: string) {
  if (next === locale) return
  locale = next
  voice = undefined
  for (const l of listeners) l()
}

/** The trip language's primary subtag ("ja", "ko"), for lang="" on cards and stickers. */
export function contentLang(): string {
  return locale.split('-')[0] ?? 'en'
}

/**
 * The language of a piece of script, for lang="": Hangul is Korean and kana Japanese whatever the
 * trip's language (any island can show on any trip: Seoul's words on a Japan trip); anything
 * else is the trip's language.
 */
export function scriptLang(text: string): string {
  if (/[가-힣ㄱ-ㆎ]/.test(text)) return 'ko'
  if (/[぀-ヿ]/.test(text)) return 'ja'
  return contentLang()
}

/** A voice for the trip's language is installed (named for the pilot's Japanese; any language). */
export function hasJapaneseVoice(): boolean {
  voice ??= pickVoice()
  return !!voice
}

/**
 * This phone can speak the trip's language: a voice is installed, or it isn't known yet (the voice
 * list is still arriving). False means "No voice on this phone" (WI-26).
 */
export function voiceReady(): boolean {
  return hasJapaneseVoice() || !voicesSettled
}

/** `text` can be heard here: a recorded clip, or the phone's voice. */
export const canSay = (text: string) => !!text && (hasClip(text) || voiceReady())

export function say(text: string, rate = 0.9) {
  if (!text) return
  const clip = clips[text]
  if (clip && typeof Audio !== 'undefined') {
    stopSpeaking()
    const n = ++sayN
    // WI-25: card clips sit about 10 dB under the places' voices: lifted, through the limiter.
    void playFile(clipBase + clip, { gainDb: CARD_GAIN_DB }).then((p) => {
      if (n !== sayN) return p?.stop() // another card spoke meanwhile
      if (p) {
        current = p
        return
      }
      // No running audio context (sound off, not unlocked) or no decode: the plain element, and
      // a clip that won't play at all (not cached, offline on a first run) falls back to the voice.
      playing = new Audio(clipBase + clip)
      void playing.play().catch(() => n === sayN && speak(text, rate))
    })
    return
  }
  speak(text, rate)
}

function speak(text: string, rate: number) {
  if (typeof speechSynthesis === 'undefined') return
  voice ??= pickVoice()
  speechSynthesis.cancel()
  const u = new SpeechSynthesisUtterance(text)
  u.lang = locale
  u.rate = rate
  if (voice) u.voice = voice
  speechSynthesis.speak(u)
}

export function stopSpeaking() {
  sayN++
  playing?.pause()
  playing = null
  current?.stop()
  current = null
  if (typeof speechSynthesis !== 'undefined') speechSynthesis.cancel()
}
